<template>
  <modal :state="state" :index="index" @toggle-modal="cancel">
    <div slot="body">
      <h3 class="modal-title">Custom Confirmation</h3>
      <div class="form-style">
        <row>
          <column class="row-title" size="100">
            <label for="header-field" class="v-label">Header:</label>
          </column>
          <column>
            <input type="text" id="header-field" placeholder="Type something" v-model="confirmation.header" @input="confirmationChanged">
          </column>
        </row>
        <row>
          <column>
            <label for="body-field">Body:</label>
            <wysiwyg
              id="body-field"
              name="body-field"
              v-model="confirmation.body"
              placeholder="Type your message here"
              :config-options="bodyWysiwygConfigOptions"
              :dynamic-options="bodyDynamicOptions"
              @input="confirmationChanged"
            ></wysiwyg>
          </column>
        </row>
      </div>

      <div class="cta">
        <button class="btn red outline block" @click="cancel">Cancel</button>
        <button class="btn green block" :disabled="!isComplete" @click="save">Save</button>
      </div>
    </div>
  </modal>
</template>

<script lang="ts">
  import Vue from "vue";
  import holdChangesUntilSaveMixin from "~/mixins/holdChangesUntilSaveMixin";
  import ObjectModelAction from "../../../common/models/objectModelActions/objectModelAction";
  import ActionConfirmation from "../../../common/models/objectModelActions/actionConfirmation";
  import Wysiwyg from "../../../common/components/wysiwyg/wysiwyg.vue";
  import ContextMenu from "../../../common/components/contextMenu.vue";
  import modalComponentMixin from "../../../common/mixins/modalComponentMixin";
  import ObjectModelPropertyDynamicFieldsHandler from "../../../common/models/dynamicFields/handlers/objectModelPropertyDynamicFieldsHandler";
  import {ObjectModelActionType} from "../../../common/enums/objectModelActionType";
  import selectedMixin from "../mixins/selectedMixin";
  import propertyUpdateMixin from "../mixins/propertyUpdateMixin";
  import {ObjectModelPropertyRootType} from "~/enums/objectModelPropertyType";
  import ObjectModelProperty from "~/models/objectModelProperties/objectModelProperty";
  import {PickListSelectionType} from "~/enums/pickListSelectionType";
  import {ArrayHelper} from "~/helpers/arrayHelper";
  import {MultiPicklistUpdateMode} from "~/enums/multiPicklistUpdateMode";

  export default Vue.extend({
    props: {
      action: Object as () => ObjectModelAction,
      confirmation: Object as () => ActionConfirmation,
    },
    mixins: [modalComponentMixin, holdChangesUntilSaveMixin, selectedMixin, propertyUpdateMixin],
    components: {
      Wysiwyg,
      ContextMenu
    },
    data: function() {
      return {
        holdChangesModel: this.confirmation,
        holdChangesParentModel: this.action,
      };
    },
    computed: {
      isComplete() {
        const {header, body} = this.confirmation;
        return header.length > 0 && body.length > 0;
      },
      bodyWysiwygConfigOptions() {
        return {
          toolbarButtons: ['bold', 'italic', 'underline'],
        };
      },
      bodyDynamicOptions() {
        return [
          {
            is: 'dynamic-fields-tree',
            button: {
              label: 'Dynamic Field',
              shortcut: {keyCode: 68, letter: 'D'},
            },
            props: {
              fieldsHandler: new ObjectModelPropertyDynamicFieldsHandler({
                mode: 'type',
                objectModel: this.selectedObjectModel,
              }),
            },
          }
        ];
      }
    },
    mounted() {
      if (this.action.action_type === ObjectModelActionType.ChangeFieldValues) {
        if (!this.confirmation.has_user_edited) {
          this.confirmation.header = 'Are You Sure?';
          const items = this.action.propertyUpdatesArray.map(propertyUpdate => {
            const property: ObjectModelProperty = this.getObjectModelProperty(propertyUpdate.property_client_id);

            if (property.property_type === ObjectModelPropertyRootType.PickList && property.selection_type === PickListSelectionType.MultiSelect) {
              const value = property.objectModelPropertiesArray.filter(x => propertyUpdate.values_to_update_to_array.includes(x.client_id)).map(x => x.label_title).join(', ');
              switch (propertyUpdate.multi_picklist_mode) {
                case MultiPicklistUpdateMode.Add:
                  return `add ${value} to ${property.labelTitleDisplay}`;
                case MultiPicklistUpdateMode.Replace:
                  return `replace ${property.labelTitleDisplay} with ${value}`;
                case MultiPicklistUpdateMode.Remove:
                  return `remove ${value} from ${property.labelTitleDisplay}`;
                case MultiPicklistUpdateMode.Clear:
                  return `clear all values from ${property.labelTitleDisplay}`;
              }
            } else {
              return `change ${property.labelTitleDisplay} to ${this.displayUpdatedPropertyValue(propertyUpdate)}`;
            }
          });

          if (items.length > 0) {
            this.confirmation.body = `You are about to <strong>${ArrayHelper.toSentence(items)}</strong>. Are you sure you want to proceed?`;
          }
          this.confirmation.has_user_edited = false;
        }
      }
    },
    methods: {
      confirmationChanged() {
        this.confirmation.has_user_edited = true;
      }
    },
    watch: {
      'confirmation.header': function() {
        this.confirmationChanged();
      },
      'confirmation.body': function() {
        this.confirmationChanged();
      },
    }
  });
</script>